/** Pure-data shape illustration model (no images / base64). */

export type ShapeType = "circle" | "rect" | "triangle" | "roundRect" | "line";

export interface ShapeInstance {
  id: string;
  type: ShapeType;
  /** Logical stage coordinates — stage is always 800 × 600. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Degrees. */
  rotation: number;
  /** One of the fixed palette values. */
  color: string;
  z: number;
}

export interface DesignSubmission {
  shapes: ShapeInstance[];
}

export const STAGE_W = 800;
export const STAGE_H = 600;
export const MAX_SHAPES = 12;

export const SHAPE_TYPES: readonly ShapeType[] = [
  "circle",
  "rect",
  "triangle",
  "roundRect",
  "line",
] as const;

export const DESIGN_PALETTE = [
  "#FF3B30",
  "#FF9500",
  "#FFCC00",
  "#34C759",
  "#007AFF",
  "#AF52DE",
  "#FFFFFF",
  "#1C1C1E",
] as const;

export type DesignPaletteColor = (typeof DESIGN_PALETTE)[number];

/** Minimal design when the writing timer forces a submit with no shapes. */
export function timeoutPlaceholderDesign(): DesignSubmission {
  return {
    shapes: [
      {
        id: "timeout",
        type: "circle",
        x: STAGE_W / 2 - 40,
        y: STAGE_H / 2 - 40,
        w: 80,
        h: 80,
        rotation: 0,
        color: "#1C1C1E",
        z: 0,
      },
    ],
  };
}

const SHAPE_TYPE_SET = new Set<string>(SHAPE_TYPES);
const PALETTE_SET = new Set<string>(DESIGN_PALETTE);

function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Validate and clamp a client design payload.
 * Returns null if unusable (empty, wrong shape, etc.).
 */
export function sanitizeDesign(raw: unknown): DesignSubmission | null {
  if (!raw || typeof raw !== "object") return null;
  const shapesRaw = (raw as { shapes?: unknown }).shapes;
  if (!Array.isArray(shapesRaw) || shapesRaw.length === 0) return null;
  if (shapesRaw.length > MAX_SHAPES) return null;

  const shapes: ShapeInstance[] = [];
  for (const item of shapesRaw) {
    if (!item || typeof item !== "object") return null;
    const s = item as Record<string, unknown>;
    if (typeof s.id !== "string" || !s.id || s.id.length > 64) return null;
    if (typeof s.type !== "string" || !SHAPE_TYPE_SET.has(s.type)) return null;
    if (typeof s.color !== "string" || !PALETTE_SET.has(s.color)) return null;
    if (
      !isFiniteNumber(s.x) ||
      !isFiniteNumber(s.y) ||
      !isFiniteNumber(s.w) ||
      !isFiniteNumber(s.h) ||
      !isFiniteNumber(s.rotation) ||
      !isFiniteNumber(s.z)
    ) {
      return null;
    }
    // Soft bounds — allow slight overflow from drag, reject absurd values
    if (
      Math.abs(s.x) > STAGE_W * 2 ||
      Math.abs(s.y) > STAGE_H * 2 ||
      s.w < 1 ||
      s.h < 1 ||
      s.w > STAGE_W * 2 ||
      s.h > STAGE_H * 2
    ) {
      return null;
    }
    shapes.push({
      id: s.id,
      type: s.type as ShapeType,
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
      rotation: ((s.rotation % 360) + 360) % 360,
      color: s.color,
      z: Math.round(s.z),
    });
  }

  return { shapes };
}
